import React, { useState, useEffect } from "react";
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { Form } from "../types";
import FormEditor from "../components/forms/FormEditor";
import { PaginationControls } from "../components/common/PaginationControls";
import toast from "../utils/toast";
import axios from "axios";
import { useTranslation } from "react-i18next";

export const FormsManagement: React.FC = () => {
  const { t } = useTranslation(["forms", "common", "errors"]);
  const { user } = useAuth();
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showFormEditor, setShowFormEditor] = useState(false);
  const [editingForm, setEditingForm] = useState<Form | null>(null);
  const [viewingForm, setViewingForm] = useState<Form | null>(null);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      setLoading(true);
      const formsRes = await axios.get("/api/forms");
      const formsList = (formsRes.data.data || []) as Form[];

      // Sort by creation date
      const sorted = formsList.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setForms(sorted);
    } catch (error) {
      console.error("Error fetching forms:", error);
      toast.error(t("errors:loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const filteredForms = forms.filter(
    (form) =>
      form.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (form.description &&
        form.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredForms.length / itemsPerPage)
  );
  const paginatedForms = filteredForms.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleCreateForm = () => {
    setEditingForm(null);
    setViewingForm(null);
    setShowFormEditor(true);
  };

  const handleEditForm = (form: Form) => {
    setEditingForm(form);
    setViewingForm(null);
    setShowFormEditor(true);
  };

  const handleViewForm = (form: Form) => {
    setViewingForm(form);
    setEditingForm(null);
    setShowFormEditor(true);
  };

  const handleSaveForm = async (formData: Partial<Form>) => {
    try {
      if (editingForm?.id) {
        // Update existing form
        await axios.put(`/api/forms/${editingForm.id}`, formData);
        toast.success(t("forms:formUpdated"));
      } else {
        // Create new form
        await axios.post("/api/forms", formData);
        toast.success(t("forms:formCreated"));
      }
      setShowFormEditor(false);
      setEditingForm(null);
      setViewingForm(null);
      fetchForms();
    } catch (error) {
      console.error("Error saving form:", error);
      toast.error(t("errors:saveFailed"));
    }
  };

  const handleCancelEditor = () => {
    setShowFormEditor(false);
    setEditingForm(null);
    setViewingForm(null);
  };

  const handleDeleteForm = async (form: Form) => {
    if (!window.confirm(t("forms:confirmDelete", { itemType: t("forms:form") }))) {
      return;
    }

    try {
      await axios.delete(`/api/forms/${form.id}`);
      toast.success(t("forms:formDeleted"));
      fetchForms();
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error(t("errors:deleteFailed"));
    }
  };

  if (loading) {
    return (
      <div className="app-page bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="app-page bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50 pt-14 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-4 flex justify-stretch sm:justify-end">
          <button
            onClick={handleCreateForm}
            className="inline-flex w-full items-center justify-center px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all font-medium group sm:w-auto"
          >
            <PlusIcon className="h-5 w-5 mr-2 group-hover:rotate-90 transition-transform" />
            {t("forms:createForm")}
          </button>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t("forms:searchForm")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Forms Table */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
          {filteredForms.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-gray-400 mb-4">
                <DocumentTextIcon className="mx-auto h-12 w-12" />
              </div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">
                {t("forms:noForms")}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchTerm
                  ? t("forms:noSearchResults", {
                      itemType: t("forms:form"),
                    })
                  : t("forms:createFirstForm")}
              </p>
              {!searchTerm && (
                <button
                  onClick={handleCreateForm}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  İlk Formu Oluştur
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="hidden lg:block bg-gradient-to-r from-slate-50 to-blue-50/50 border-b border-slate-200 px-5 py-2.5">
                <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  <div className="col-span-4">{t("forms:formTitle")}</div>
                  <div className="col-span-3">{t("common:status")}</div>
                  <div className="col-span-2">{t("forms:fieldCount")}</div>
                  <div className="col-span-3 text-right">
                    {t("common:actions")}
                  </div>
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-slate-100">
                {paginatedForms.map((form) => {
                  const isArchived = "isArchived" in form && form.isArchived;
                  const fieldCount = form.formStructure?.elements?.length || 0;

                  return (
                    <div
                      key={form.id}
                      className={`relative group hover:bg-blue-50/30 transition-all duration-200 ${
                        isArchived ? "opacity-60" : ""
                      }`}
                    >
                      <div className="px-4 lg:px-5 py-3">
                        {/* Desktop Layout */}
                        <div className="hidden lg:grid lg:grid-cols-12 gap-4 items-center">
                          {/* Title */}
                          <div className="col-span-4">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center shadow-sm">
                                  <DocumentTextIcon className="w-4 h-4 text-white" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-xs font-bold text-slate-800 truncate group-hover:text-blue-700 transition-colors">
                                  {form.title}
                                </h3>
                                {form.description && (
                                  <p className="text-xs text-slate-500 truncate">
                                    {form.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Status Badges */}
                          <div className="col-span-3 flex flex-wrap gap-1">
                            {form.isPublic && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                {t("activities:isPublic")}
                              </span>
                            )}
                            {isArchived && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                {t("common:archive")}
                              </span>
                            )}
                            {"version" in form && form.version && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                                v{form.version}
                              </span>
                            )}
                          </div>

                          {/* Field Count */}
                          <div className="col-span-2">
                            <span className="text-sm font-medium text-slate-700">
                              {fieldCount} {t("forms:field")}
                            </span>
                          </div>

                          {/* Actions */}
                          <div className="col-span-3 flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleViewForm(form)}
                              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                              title={t("common:view")}
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditForm(form)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                              title={t("common:edit")}
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteForm(form)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                              title={t("common:delete")}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Mobile Layout */}
                        <div className="lg:hidden">
                          <div className="flex items-start gap-2">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center shadow-sm flex-shrink-0">
                                <DocumentTextIcon className="w-4 h-4 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-xs font-bold text-slate-800 truncate">
                                  {form.title}
                                </h3>
                                <p className="text-xs text-slate-500">
                                  {fieldCount} {t("forms:field")}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {form.isPublic && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                {t("activities:isPublic")}
                              </span>
                            )}
                            {"version" in form && form.version && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                                v{form.version}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 flex justify-end gap-1">
                            <button
                              onClick={() => handleViewForm(form)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-green-50 text-green-700"
                              title={t("common:view")}
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditForm(form)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-blue-700"
                              title={t("common:edit")}
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteForm(form)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-red-50 text-red-700"
                              title={t("common:delete")}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-slate-200 bg-slate-50/80 px-4 lg:px-5 py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredForms.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    accent="blue"
                    className="mt-0"
                  />

                  <div className="ml-auto flex items-center gap-2 text-xs text-slate-600">
                    <span>Göster</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Form Editor Modal */}
      {showFormEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div className="h-full w-full">
            <FormEditor
              form={(editingForm || viewingForm) as Form}
              onSave={handleSaveForm}
              onCancel={handleCancelEditor}
              readonly={!!viewingForm}
            />
          </div>
        </div>
      )}
    </div>
  );
};
